import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Chip, Icon, ListGroup, ListRow, MoneyField, Option, OptionList, Skeleton } from '@uplink/ui';
import { useEffect, useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import cryptoBotLogo from '../assets/cryptobot.jpg';
import xRocketLogo from '../assets/xrocket.jpg';
import { formatUsd, parseUsdInput } from '../format.ts';
import { MainActionBar } from '../native-controls.tsx';
import type { Nav, Purchase } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { haptic, openPayUrl } from '../telegram.ts';

type Method = 'cryptopay' | 'xrocket';
/** Each payment system's own logo and bot, so people recognise where they pay */
const METHODS: Record<Method, { name: string; bot: string; logo: string }> = {
  cryptopay: { name: 'CryptoBot', bot: '@CryptoBot', logo: cryptoBotLogo },
  xrocket: { name: 'xRocket', bot: '@xRocket', logo: xRocketLogo },
};

export function TopUpScreen({
  nav,
  initialCents,
  after,
}: {
  nav: Nav;
  initialCents?: number;
  /** Placed by itself once the money arrives (topped up for a missing amount) */
  after?: Purchase;
}) {
  const trpc = useTRPC();
  const { t, money, currency, ratesReady } = usePrefs();
  const queryClient = useQueryClient();
  const config = useQuery(trpc.config.queryOptions());
  const [amount, setAmount] = useState(
    initialCents ? (initialCents / 100).toFixed(2).replace(/\.00$/, '') : '50',
  );
  const [created, setCreated] = useState<{ id: string; payUrl: string } | null>(null);
  const methods = (config.data?.paymentMethods ?? ['cryptopay']) as Method[];
  const [picked, setPicked] = useState<Method | null>(null);
  const method: Method = picked && methods.includes(picked) ? picked : (methods[0] ?? 'cryptopay');
  const via = METHODS[method].name;

  const cents = parseUsdInput(amount);
  const min = config.data?.topupMinCents ?? 500;
  const max = config.data?.topupMaxCents ?? 1_000_000;
  const valid = cents !== null && cents >= min && cents <= max;

  const create = useMutation(
    trpc.topups.create.mutationOptions({
      onSuccess: (t) => {
        setCreated({ id: t.id, payUrl: t.payUrl });
        openPayUrl(t.payUrl);
      },
      onError: () => haptic.error(),
    }),
  );

  const status = useQuery({
    ...trpc.topups.get.queryOptions({ id: created?.id ?? '00000000-0000-0000-0000-000000000000' }),
    enabled: !!created,
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 3000 : false),
  });
  const paid = status.data?.status === 'paid';
  const summary = useQuery(trpc.balance.summary.queryOptions());

  useEffect(() => {
    if (!paid) return;
    haptic.success();
    queryClient.invalidateQueries(trpc.balance.pathFilter());
  }, [paid, queryClient, trpc]);

  // The purchase this top-up was for, placed as soon as the money is in
  const domainOrder = useMutation(trpc.domains.order.mutationOptions());
  const serverOrder = useMutation(trpc.servers.order.mutationOptions());
  const domainRenew = useMutation(trpc.domains.renew.mutationOptions());
  const serverRenew = useMutation(trpc.servers.renew.mutationOptions());
  const [resume, setResume] = useState<'idle' | 'running' | 'failed'>('idle');
  useEffect(() => {
    if (!paid || !after || resume !== 'idle') return;
    setResume('running');
    const fail = () => {
      haptic.error();
      setResume('failed');
    };
    const finish = (go: () => void) => () => {
      haptic.success();
      for (const f of [trpc.balance, trpc.domains, trpc.servers])
        queryClient.invalidateQueries(f.pathFilter());
      go();
    };
    switch (after.kind) {
      case 'domainOrder':
        domainOrder.mutate(
          { domain: after.domain, contactId: after.contactId },
          { onSuccess: finish(() => nav.reset('domains')), onError: fail },
        );
        break;
      case 'serverOrder':
        serverOrder.mutate(
          {
            planId: after.planId,
            imageId: after.imageId,
            name: after.name,
            sshPublicKey: after.sshPublicKey,
            vpn: after.vpn,
          },
          {
            onSuccess: (r) =>
              finish(() => {
                nav.reset('servers');
                nav.push({ name: 'server', id: r.id });
              })(),
            onError: fail,
          },
        );
        break;
      case 'domainRenew':
        domainRenew.mutate({ id: after.id }, { onSuccess: finish(nav.back), onError: fail });
        break;
      case 'serverRenew':
        serverRenew.mutate({ id: after.id }, { onSuccess: finish(nav.back), onError: fail });
        break;
    }
  }, [paid, after, resume, domainOrder, serverOrder, domainRenew, serverRenew, nav, queryClient, trpc]);

  if (created && paid && after && resume !== 'failed') {
    return (
      <main className="ul-screen">
        <div className="ul-wait" aria-live="polite">
          <span className="ul-spinner" aria-hidden />
          <span className="ul-wait__title">{t.topup.resuming(after.label)}</span>
          <span className="ul-wait__text">{t.topup.resumingText}</span>
        </div>
      </main>
    );
  }

  if (created && paid) {
    return (
      <main className="ul-screen ul-screen--with-action">
        <div className="ul-success" aria-live="polite">
          <span className="ul-success__mark">
            <Icon name="check" size={32} />
          </span>
          <span className="ul-success__amount">{formatUsd(status.data!.amountCents)}</span>
          <span className="ul-success__text">
            {resume === 'failed' ? t.topup.resumeFailed : t.topup.paid(status.data!.paidAsset ?? null)}
          </span>
        </div>
        {/* Opened from checkout with a missing amount: go back to finish the purchase */}
        <MainActionBar
          action={
            initialCents
              ? { text: t.topup.backToOrder, onClick: () => nav.back() }
              : { text: t.common.done, onClick: () => nav.reset() }
          }
        />
      </main>
    );
  }

  if (created) {
    const expired = status.data?.status === 'expired';
    return (
      <main className="ul-screen ul-screen--with-action">
        <h1 className="ul-screen__title">{t.topup.title}</h1>
        <div className="ul-wait" aria-live="polite">
          <span className="ul-sig ul-sig--provisioning" aria-hidden>
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="ul-sig__bar is-on"
                style={{ height: 4 + i * 4, animationDelay: `${i * 120}ms` }}
              />
            ))}
          </span>
          <span className="ul-wait__title">{expired ? t.topup.expired : t.topup.waiting(via)}</span>
          <span className="ul-wait__text">
            {expired
              ? t.topup.expiredText
              : t.topup.waitingText(formatUsd(status.data?.amountCents ?? cents ?? 0))}
          </span>
        </div>
        <MainActionBar
          action={
            expired
              ? { text: t.topup.newInvoice, onClick: () => setCreated(null) }
              : { text: t.topup.reopen, onClick: () => openPayUrl(created.payUrl) }
          }
        />
      </main>
    );
  }

  return (
    <main className="ul-screen ul-screen--with-action">
      <h1 className="ul-screen__title">{t.topup.title}</h1>

      <div className="ul-stack">
        <div className="ul-group__header">{t.topup.amount}</div>
        <MoneyField
          id="topup-amount"
          label={t.topup.amount}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        {valid && currency !== 'USD' && ratesReady ? (
          <div className="ul-group__footer">{t.topup.approx(money(cents))}</div>
        ) : null}
        {config.isPending ? (
          <Skeleton height={32} />
        ) : (
          <div className="ul-chips">
            {config.data?.topupPresetsCents.map((p) => (
              <Chip
                key={p}
                selected={cents === p}
                onClick={() => {
                  haptic.select();
                  setAmount(String(p / 100));
                }}
              >
                {formatUsd(p).replace('.00', '')}
              </Chip>
            ))}
          </div>
        )}
        {summary.data?.firstTopup && config.data && config.data.rewards.firstTopupBonusPct > 0 ? (
          <div className="ul-bonus-note">
            <Icon name="gift" size={16} />
            {t.topup.firstBonus(
              config.data.rewards.firstTopupBonusPct,
              formatUsd(config.data.rewards.firstTopupBonusMaxCents),
            )}
          </div>
        ) : null}
        {amount && !valid ? (
          <div className="ul-group__footer" style={{ color: 'var(--ul-crit)' }}>
            {t.topup.range(formatUsd(min), formatUsd(max))}
          </div>
        ) : null}
      </div>

      <div className="ul-stack">
        <div className="ul-group__header">{t.topup.payment}</div>
        <OptionList label={t.topup.payment}>
          {methods.map((m) => (
            <Option
              key={m}
              selected={m === method}
              onSelect={() => {
                haptic.select();
                setPicked(m);
              }}
              leading={<img className="ul-brand-mark" src={METHODS[m].logo} alt="" width={28} height={28} />}
              title={METHODS[m].bot}
              // xRocket takes USDT only (see XROCKET_CURRENCY)
              subtitle={
                m === 'xrocket' ? 'USDT' : (config.data?.acceptedAssets ?? ['USDT', 'TON', 'BTC']).join(', ')
              }
            />
          ))}
        </OptionList>
      </div>

      <ListGroup footer={t.topup.footer(valid ? formatUsd(cents) : null, via)}>
        <ListRow icon="check" title={t.topup.credit} value={valid ? formatUsd(cents) : '—'} />
      </ListGroup>

      {create.isError ? (
        <div className="ul-error" role="alert">
          {errorText(create.error)}
        </div>
      ) : null}

      <MainActionBar
        action={{
          text: valid ? t.topup.pay(formatUsd(cents), via) : t.topup.enterAmount,
          disabled: !valid,
          loading: create.isPending,
          onClick: () => valid && create.mutate({ amountCents: cents, provider: method }),
        }}
      />
    </main>
  );
}
